import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useOrg } from '@/contexts/OrgContext';
import { useCompanyInfoBrief } from '@/hooks/useCompanyInfo';
import { useLocalSeoData } from '@/hooks/useLocalSeo';
import { isTenantHost, tenantBrowserOrigin } from '@/lib/tenantHost';
import { brandDescription, brandTitle, resolvePageSeo, type SeoPage } from '@/lib/localSeo';

type PageSeoProps = {
  pageSlug: string;
  fallbackTitle: string;
  fallbackDescription?: string;
  orgIdOverride?: string | null;
  orgName?: string;
};

type PageSeoRow = {
  seo_title: string | null;
  meta_description: string | null;
  og_title: string | null;
  og_description: string | null;
  social_image_url: string | null;
  indexing_preference: string | null;
  canonical_url: string | null;
};

type PageSeoQueryBuilder = {
  select(columns: string): {
    eq(column: string, value: string | null | undefined): {
      eq(column: string, value: string): {
        maybeSingle(): Promise<{ data: PageSeoRow | null; error: Error | null }>;
      };
    };
  };
};

type PageSeoSupabaseClient = {
  from(table: 'page_seo_settings'): PageSeoQueryBuilder;
};

const BUILT_IN = new Set(['home', 'services', 'about', 'contact', 'booking']);

function seoPageFor(pageSlug: string, title: string, description?: string): SeoPage {
  return BUILT_IN.has(pageSlug)
    ? { kind: pageSlug as 'home' | 'services' | 'about' | 'contact' | 'booking' }
    : { kind: 'custom', slug: pageSlug, title, description: description ?? null };
}

export function PageSeo({ pageSlug, fallbackTitle, fallbackDescription, orgIdOverride, orgName }: PageSeoProps) {
  const { orgId, org } = useOrg();
  const targetOrgId = orgIdOverride ?? orgId;
  const { pathname } = useLocation();

  const { data } = useQuery({
    queryKey: ['page-seo', targetOrgId, pageSlug],
    enabled: Boolean(targetOrgId && pageSlug),
    queryFn: async () => {
      const { data: seo, error } = await (supabase as unknown as PageSeoSupabaseClient)
        .from('page_seo_settings')
        .select('seo_title, meta_description, og_title, og_description, social_image_url, indexing_preference, canonical_url')
        .eq('organization_id', targetOrgId)
        .eq('page_slug', pageSlug)
        .maybeSingle();

      if (error) throw error;
      return seo;
    },
  });

  const { data: companyInfo } = useCompanyInfoBrief(targetOrgId ?? undefined);
  const { data: localSeo } = useLocalSeoData(targetOrgId);

  const businessName = companyInfo?.company_name?.trim() || '';
  const staleNames = [orgName, org.name].filter(
    (name): name is string => Boolean(name && name !== businessName),
  );

  // Always surface the tenant's customer-facing company name in titles and
  // descriptions so shared/SEO previews brand the business, not "The Polished
  // Version" or an older signup/internal org name. When the owner hasn't written
  // a title/description, use the smart local default (services + areas) once
  // that data has loaded — the same text api/tenant-page.ts renders for crawlers.
  const descriptionFallback = fallbackDescription || org.tagline || (businessName ? `Book cleaning services with ${businessName}.` : 'Book cleaning services.');
  let title: string;
  let description: string;
  if (localSeo && businessName) {
    const resolved = resolvePageSeo(seoPageFor(pageSlug, fallbackTitle, fallbackDescription), {
      businessName,
      company: localSeo.company,
      services: localSeo.services,
      row: data ? { page_slug: pageSlug, ...data } : null,
      staleNames,
    });
    title = resolved.title;
    description = resolved.description;
  } else {
    title = brandTitle(data?.seo_title || fallbackTitle || businessName || 'Cleaning services', businessName, staleNames, fallbackTitle);
    description = brandDescription(data?.meta_description || descriptionFallback, businessName, staleNames, descriptionFallback);
  }
  const ogTitle = brandDescription(data?.og_title || title, businessName, staleNames, title);
  const ogDescription = brandDescription(data?.og_description || description, businessName, staleNames, description);
  const robots = data?.indexing_preference === 'noindex' ? 'noindex,nofollow' : 'index,follow';

  // Canonical on the tenant's own origin. index.html ships the platform's
  // canonical; api/tenant-page.ts replaces it for crawlers, and here we take it
  // over in the browser so client-side navigation keeps it correct.
  const onTenantHost = isTenantHost();
  const canonical = data?.canonical_url || (onTenantHost && localSeo
    ? `${tenantBrowserOrigin(localSeo.customDomain)}${pathname === '/' ? '/' : pathname.replace(/\/+$/, '')}`
    : null);
  useEffect(() => {
    if (!canonical) return;
    document.querySelectorAll('link[rel="canonical"]:not([data-rh])').forEach((el) => el.remove());
  }, [canonical]);

  // Share image precedence: the page's own social image, then the tenant's
  // uploaded company logo, then the org logo, then a safe generic fallback. This
  // overrides the static Polished Version branding in index.html so tenant pages
  // never share that imagery when tenant branding exists.
  const socialImage = data?.social_image_url || companyInfo?.logo_url || org.logoUrl || '/og-image.png';

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={socialImage} />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={socialImage} />
      {canonical && <link rel="canonical" href={canonical} />}
      {canonical && <meta property="og:url" content={canonical} />}
    </Helmet>
  );
}
